import { useMemo, useState } from "react"
import { toast } from "sonner"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Alert01Icon,
  ArrowRight01Icon,
  Loading01Icon,
  Tick01Icon,
} from "@hugeicons/core-free-icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@/components/ui/input-group"
import { Label } from "@/components/ui/label"
import { addCustomSiteToWorkspace, getUserFacingApiError } from "@/lib/api/api"
import { createWorkspace, getAllWorkspaces } from "@/lib/api/workspaces"
import { useAuth } from "@/lib/contexts/AuthContext"
import { normalizeDomain } from "@/lib/utils/domainUtils"
import { track } from "@/lib/utils/track"
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress"
import { cn } from "@/lib/utils"

type OnboardingGateProps = {
  onSiteAdded?: () => void | Promise<void>
}

function deriveSiteName(domain: string): string {
  const base = domain.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ")
  return (
    base
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") || domain
  )
}

const STEP_META = {
  1: {
    subtitle: "Step 1 of 3",
    title: "Add Your Website",
    description: "Enter your website domain to start tracking",
  },
  2: {
    subtitle: "Step 2 of 3",
    title: "Connect your tracking",
    description: "Add Humblytics to your site so we can prepare your first test.",
  },
  3: {
    subtitle: "Step 3 of 3",
    title: "Launch first test",
    description: "Use your newly connected site to start an experiment.",
  },
} as const

const AGENT_MESSAGE = "Once connected, I can suggest A/B test ideas for your site."

export function OnboardingGate({ onSiteAdded }: OnboardingGateProps) {
  const { token, user } = useAuth()
  const progress = useOnboardingProgress()
  const [domain, setDomain] = useState("")
  const [domainError, setDomainError] = useState("")
  const [isCreatingSite, setIsCreatingSite] = useState(false)

  const normalizedDomain = useMemo(() => normalizeDomain(domain), [domain])
  // Soft warning (amber, non-blocking): raw input still carries a protocol or
  // "www." — it will be stripped automatically, so we hint rather than error.
  const hasSoftWarning = domain.trim() !== "" && !domainError && /^https?:\/\/|^www\./i.test(domain.trim())
  // The gate now only mounts for an account with zero sites (WorkspaceHub),
  // and adding one refetches and unmounts it. Unverified tracking is handled
  // on the hub by TrackingNotInstalledBanner, so the install and launch
  // branches that used to live here are gone: one install surface, not two.
  const isAddSiteStep = progress.currentStep === 1 && !progress.firstProperty
  const meta = STEP_META[(progress.currentStep as 1 | 2 | 3) || 1]

  const handleDomainChange = (value: string) => {
    setDomain(value)
    setDomainError("")
  }

  const ensureWorkspaceId = async (): Promise<string> => {
    if (!token) throw new Error("Missing auth token")
    const workspaces = await getAllWorkspaces(token)
    if (workspaces?.length) return workspaces[0].workspaceId
    const name = user?.name ? `${user.name}'s Workspace` : "My Workspace"
    const workspace = await createWorkspace(name, undefined, token)
    return workspace.workspaceId
  }

  const handleCreateSite = async () => {
    if (!token) return
    if (!normalizedDomain) {
      setDomainError("Enter domain without 'www' or 'http://' (e.g., example.com)")
      return
    }

    setIsCreatingSite(true)
    try {
      const workspaceId = await ensureWorkspaceId()
      const response = await addCustomSiteToWorkspace(
        workspaceId,
        normalizedDomain,
        deriveSiteName(normalizedDomain),
        false,
        token,
        true,
        true,
      )

      if (response?.duplicate) {
        toast.success("Site already added. Continue by connecting tracking.")
        track("dashboard_onboarding_site_duplicate", token, { domain: normalizedDomain })
      } else {
        toast.success("Site added. Next, connect your tracker.")
        track("dashboard_onboarding_site_added", token, {
          domain: normalizedDomain,
          workspace_id: workspaceId,
        })
      }

      await progress.refetch()
      await onSiteAdded?.()
    } catch (error) {
      toast.error(getUserFacingApiError(error, "Failed to add site. Please try again."))
    } finally {
      setIsCreatingSite(false)
    }
  }


  return (
    <Card className="border-border/30 py-0 shadow-sm">
      <CardContent className="p-0">
        <div className="grid lg:grid-cols-[minmax(220px,0.3fr)_minmax(0,0.7fr)]">
          <aside className="border-b border-border/30 bg-muted/20 p-5 lg:border-b-0 lg:border-r lg:p-6">
            <p className="text-xs font-semibold text-primary">
              Setup Progress
            </p>
            <h3 className="mt-2 text-sm font-semibold text-foreground">Getting started</h3>

            <nav className="mt-6 space-y-2">
              {progress.steps.map((step) => {
                const isCurrent = step.n === progress.currentStep && !step.done
                const isDone = step.done

                return (
                  <div
                    key={step.id}
                    className={cn(
                      "flex items-center gap-2.5 rounded-md px-2 py-2 text-xs transition-colors",
                      isCurrent && "bg-background shadow-sm ring-1 ring-border/60",
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold transition-all",
                        isDone && "bg-muted text-foreground",
                        isCurrent && "bg-primary text-primary-foreground",
                        !isDone && !isCurrent && "bg-muted/50 text-muted-foreground",
                      )}
                    >
                      {isDone ? <HugeiconsIcon icon={Tick01Icon} size={12} /> : step.n}
                    </div>
                    <span
                      className={cn(
                        "leading-none",
                        isCurrent ? "font-semibold text-foreground" : "text-muted-foreground",
                        isDone && !isCurrent && "text-foreground/60",
                      )}
                    >
                      {step.label}
                    </span>
                  </div>
                )
              })}
            </nav>
          </aside>

          <section className="relative overflow-hidden bg-background p-5 sm:p-6 lg:p-8">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.35]"
              aria-hidden
              style={{
                backgroundImage:
                  "radial-gradient(circle, var(--color-border) 1px, transparent 1px)",
                backgroundSize: "24px 24px",
              }}
            />
            <div
              className="pointer-events-none absolute inset-0"
              aria-hidden
              style={{
                background:
                  "radial-gradient(ellipse at center, transparent 30%, var(--color-background) 75%)",
              }}
            />

            <div className="relative z-[1] mx-auto max-w-3xl">
              <div className="mb-4 space-y-1">
                <p className="text-xs font-semibold text-primary/80">
                  {meta.subtitle}
                </p>
                <h2 className="text-xl font-semibold tracking-tight text-foreground">
                  {meta.title}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {meta.description}
                </p>
              </div>

              <Card className="border-border/30 shadow-sm">
                <CardContent className="p-6">
                  {progress.isLoading || progress.isFetching ? (
                    <div className="flex items-center gap-2 rounded-lg border border-border/30 bg-muted/20 px-3 py-4 text-sm text-muted-foreground">
                      <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />
                      Loading setup progress...
                    </div>
                  ) : isAddSiteStep ? (
                    <form
                      className="space-y-4"
                      onSubmit={(event) => {
                        event.preventDefault()
                        handleCreateSite()
                      }}
                    >
                      <div className="space-y-1 border-b border-border/30 pb-4">
                        <p className="text-xs font-medium text-foreground">Humblytics will help you:</p>
                        <ul className="mt-2 space-y-1">
                          {[
                            "Run A/B tests without touching code",
                            "See exactly which pages are losing you revenue",
                            "Connect ad spend to real signups and sales",
                            "Get AI recommendations from your own traffic data",
                          ].map((item) => (
                            <li key={item} className="flex items-start gap-2 text-[11px] text-muted-foreground">
                              <span className="shrink-0 text-primary">-&gt;</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="flex items-center gap-2.5 rounded-md bg-muted/40 px-3 py-2">
                        <div className="relative shrink-0">
                          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted">
                            <span className="text-[11px] leading-none text-muted-foreground">*</span>
                          </div>
                          <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full border border-background bg-live" />
                        </div>
                        <p className="text-[11px] text-muted-foreground">{AGENT_MESSAGE}</p>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="dashboard-onboarding-domain" className="text-xs">
                          Domain <span className="text-destructive">*</span>
                        </Label>
                        <InputGroup
                          className={cn(
                            "h-9",
                            hasSoftWarning && "border-warning/60 dark:border-warning/50",
                            domainError && "border-destructive ring-[2px] ring-destructive/20",
                          )}
                        >
                          <InputGroupAddon>
                            <InputGroupText>https://</InputGroupText>
                          </InputGroupAddon>
                          <InputGroupInput
                            id="dashboard-onboarding-domain"
                            placeholder="example.com"
                            value={domain}
                            onChange={(event) => handleDomainChange(event.target.value)}
                            autoCapitalize="none"
                            autoCorrect="off"
                            autoComplete="off"
                            aria-invalid={!!domainError}
                          />
                          {domain && !domainError && !hasSoftWarning && (
                            <InputGroupAddon align="inline-end">
                              <HugeiconsIcon icon={Tick01Icon} size={14} className="text-success dark:text-success" />
                            </InputGroupAddon>
                          )}
                          {hasSoftWarning && (
                            <InputGroupAddon align="inline-end">
                              <HugeiconsIcon icon={Alert01Icon} size={14} className="text-warning" />
                            </InputGroupAddon>
                          )}
                        </InputGroup>
                        {domainError ? (
                          <p className="text-[11px] text-destructive">{domainError}</p>
                        ) : hasSoftWarning ? (
                          <p className="text-[11px] text-warning dark:text-warning">
                            No need for "www" or "https://". We'll strip that automatically.
                          </p>
                        ) : null}
                      </div>

                      <Button
                        type="submit"
                        disabled={isCreatingSite}
                        className="h-9 w-full gap-2"
                      >
                        {isCreatingSite ? (
                          <>
                            <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />
                            Adding...
                          </>
                        ) : (
                          <>
                            Next
                            <HugeiconsIcon icon={ArrowRight01Icon} size={14} />
                          </>
                        )}
                      </Button>
                    </form>
                  ) : (
                    <div className="rounded-lg border border-border/30 bg-muted/20 px-3 py-4 text-sm text-muted-foreground">
                      Add your first site to continue setup.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </section>
        </div>
      </CardContent>
    </Card>
  )
}
